import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { ErrorState } from '@adornis/components/client/ErrorState.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { Validator } from '@adornis/validation/validation.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { addMonths, addYears, startOfMonth } from 'date-fns';
import { html } from 'lit';
import type { ObservedValueOf } from 'rxjs';
import { BookingAPI } from '../../bookings/api/BookingAPI.js';
import { ClassBooking, Grade } from '../../bookings/entities/ClassBooking.js';
import { getAllInternalMartinusSchools } from '../../groups/api/getAllSchools.js';
import { getSchoolIDsForRoles } from '../../permissions/api/getSchoolIDsForRoles.js';
import { Role } from '../../permissions/roles.js';
import { getSchoolYear, getSchoolYearIntervalDatesFromDate } from '../../schoolyear/getSchoolYear.js';
import { Person } from '../entities/person.js';

// the class stays optional: the classes of a new Jahrgang are often formed only shortly before it starts
const ClassAdmissionFormValidator = {
  startDate: (options: ValidationFunctionOptions<Maybe<Date>, ClassBooking>) => {
    if (!options.value)
      throw new ValidationError('Bitte einen Beginn wählen.', { key: options.key, path: options.path });
  },
  groupIDs: (options: ValidationFunctionOptions<Maybe<string[]>, ClassBooking>) => {
    if (!options.value?.length)
      throw new ValidationError('Bitte eine Schule wählen.', { key: options.key, path: options.path });
  },
  grade: (options: ValidationFunctionOptions<Maybe<string>, ClassBooking>) => {
    if (!options.value)
      throw new ValidationError('Bitte einen Jahrgang wählen.', { key: options.key, path: options.path });
  },
} satisfies Validator;

const loadPerson = (personID: string) =>
  subscribeByID(
    Person,
    personID,
  )({
    _id: 1,
    roles: { name: 1, contexts: 1 },
    profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
    schuelerDaten: { wantedKlassenstufe: 1 },
  });

type AdmissionPerson = NonNullable<ObservedValueOf<ReturnType<typeof loadPerson>>>;
type AdmissionSchool = Awaited<ReturnType<typeof getAllInternalMartinusSchools>>[number];

const ClassAdmissionForm = stateful(
  (props: {
    person: AdmissionPerson;
    schools: AdmissionSchool[];
    appliedSchoolID: Maybe<string>;
    resolve: (created: boolean) => void;
    reject: () => void;
  }) => {
    const today = todayAsCalendarDay();
    // a year from today always lies in the next school year, whichever half of this one today is in
    const nextSchoolYearStart = getSchoolYearIntervalDatesFromDate(addYears(today, 1)).start;
    const wantedGrade = props.person.schuelerDaten?.wantedKlassenstufe;

    // prefilled once from the application, so a live update never overwrites what staff picked
    const [booking] = useState(
      new ClassBooking({
        userID: props.person._id,
        groupIDs: props.appliedSchoolID ? [props.appliedSchoolID] : [],
        startDate: nextSchoolYearStart,
        grade: wantedGrade ?? null,
      }),
      [],
    );
    const form = useForm(booking, { validator: ClassAdmissionFormValidator });
    const startDate = form.document.startDate;
    // a class booking spans exactly one school year (`bookingSchoolyearHook`), so it ends with the one it starts in
    const endDate = startDate ? getSchoolYearIntervalDatesFromDate(startDate).end : null;

    const quickStarts = [
      { label: 'Heute', date: today },
      { label: 'Nächster Monat', date: toCalendarDay(startOfMonth(addMonths(today, 1))) },
      { label: 'Übernächster Monat', date: toCalendarDay(startOfMonth(addMonths(today, 2))) },
      { label: 'Nächstes Schuljahr', date: nextSchoolYearStart },
    ];
    // the application form also offers E, Q1 and Q3, which the booking editor's grades do not list
    const grades = [...new Set([...Object.values(Grade), wantedGrade].filter(isNotNullish))];
    const schoolField = form.field('groupIDs');

    const handleConfirm = async () => {
      if (await form.validate()) return;
      const classBooking = new ClassBooking({
        userID: props.person._id,
        groupIDs: form.document.groupIDs,
        startDate,
        endDate,
        grade: form.document.grade,
        classIdentifier: form.document.classIdentifier?.trim() || null,
      });

      await guardDialog(
        async () => {
          const result = await BookingAPI.upsert([classBooking])({ upserted: 1, skippedExisting: 1 });
          // the upsert skips a person who is already on the school for some of these days instead of failing
          if (!result?.upserted)
            throw new ExpectedError(
              'Das Kind ist in diesem Zeitraum bereits an dieser Schule gebucht. Bitte die bestehende Buchung im Buchungseditor anpassen.',
            );
        },
        { success: () => props.resolve(true) },
      );
    };

    const name = props.person.profilDaten?.stammdaten;

    return html`
      <div class="dialog-container flex flex-col gap-4">
        ${DialogHeader({
          title: `In Klasse aufnehmen · ${name?.vorname ?? ''} ${name?.nachname ?? ''}`,
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <div class="flex flex-col gap-1">
          <span class="text-sm font-semibold text-base-content/70">Beginn</span>
          <div class="flex flex-wrap items-center gap-2">
            <div>${DateInput({ ...form.field('startDate') })}</div>
            ${quickStarts.map(
              ({ label, date }) =>
                html`<button class="btn btn-sm btn-outline" @click=${() => form.field('startDate').onPicked?.(date)}>
                  ${label}
                </button>`,
            )}
          </div>
        </div>
        <p class="text-sm text-base-content/70">
          ${
            startDate && endDate
              ? `Schuljahr ${getSchoolYear({ date: startDate })}: ${formatCalendarDay(startDate, 'dd.MM.yyyy')} – ${formatCalendarDay(endDate, 'dd.MM.yyyy')}`
              : 'Die Buchung endet mit dem Schuljahr, in dem sie beginnt.'
          }
        </p>

        <div class="grid gap-3 sm:grid-cols-3">
          <div class="flex flex-col gap-1">
            <span class="text-sm font-semibold text-base-content/70">Schule</span>
            ${DropdownSelectionInput({
              value: schoolField.value?.[0] ?? null,
              error: schoolField.error,
              selectables: props.schools.map(school => school._id),
              renderString: schoolID => props.schools.find(school => school._id === schoolID)?.name ?? schoolID,
              onPicked: schoolID => schoolField.onPicked?.(schoolID ? [schoolID] : []),
              placeholder: 'Schule wählen',
              placeholderMode: 'hide',
            })}
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm font-semibold text-base-content/70">Jahrgang</span>
            ${DropdownSelectionInput({
              ...form.field('grade'),
              selectables: grades,
              placeholder: 'Jahrgang wählen',
              placeholderMode: 'hide',
            })}
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm font-semibold text-base-content/70">Klasse</span>
            ${TextInput({ ...form.field('classIdentifier'), placeholder: 'z. B. a', placeholderMode: 'hide' })}
          </div>
        </div>

        <div class="flex justify-end gap-2">
          <button class="btn btn-outline" @click=${props.reject}>Abbrechen</button>
          <button class="btn btn-primary" @click=${handleConfirm}>Klassenbuchung anlegen</button>
        </div>
      </div>
    `;
  },
);

const ClassAdmissionDialogContent = stateful(
  (props: { personID: string; resolve: (created: boolean) => void; reject: () => void }) => {
    const person = useQuery(() => loadPerson(props.personID), [props.personID]);
    const schools = useQuery(() => getAllInternalMartinusSchools(), []);
    // an applicant's school is only recorded in the role (see `calculateStudentRoles`)
    const appliedSchoolIDs = useQuery(
      () => getSchoolIDsForRoles(person.value, [Role.SUS_BEWERBER, Role.OLD_SUS_BEWERBER]),
      [person.value?._id],
    );

    const error = person.error ?? schools.error ?? appliedSchoolIDs.error;
    if (error)
      return html`<div class="dialog-container">
        ${DialogHeader({ title: 'In Klasse aufnehmen', close: props.reject, closeLabel: 'Dialog schließen' })}
        ${ErrorState({ title: 'Laden fehlgeschlagen', description: error.message })}
      </div>`;

    if (person.loading || schools.loading || appliedSchoolIDs.loading || !schools.value || !appliedSchoolIDs.value)
      return html`<div class="dialog-container flex justify-center p-8">
        <span class="loading loading-spinner loading-md"></span>
      </div>`;

    if (!person.value || !schools.value.length)
      return html`<div class="dialog-container">
        ${DialogHeader({ title: 'In Klasse aufnehmen', close: props.reject, closeLabel: 'Dialog schließen' })}
        ${ErrorState({ title: person.value ? 'Keine Schulen gefunden' : 'Person nicht gefunden' })}
      </div>`;

    return ClassAdmissionForm({
      person: person.value,
      schools: schools.value,
      appliedSchoolID: appliedSchoolIDs.value.at(0),
      resolve: props.resolve,
      reject: props.reject,
    });
  },
);

/**
 * Opens the assistant that puts a child into a class: one class booking on the school for the school
 * year it starts in. School and Jahrgang come from the application. Resolves to whether it was created.
 */
export async function openClassAdmissionDialog(personID: string): Promise<boolean> {
  try {
    return await showDialog<boolean>({
      content: (resolve, reject) => ClassAdmissionDialogContent({ personID, resolve, reject }),
      modal: true,
      modalBoxClasses: 'max-w-3xl',
    });
  } catch {
    // dialog was dismissed
    return false;
  }
}
